import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table2, Loader2, Copy, Download, Save } from 'lucide-react';
import { toast } from 'sonner';
import { thesisApi } from '@/api/thesisApi';

/**
 * Findings Table Extractor — Elicit-style multi-paper PICO comparison.
 *
 * Workflow:
 *   1. User opens dialog from the workspace
 *   2. Enters a research topic (e.g. "early aggressive fluid resuscitation in acute pancreatitis")
 *   3. Backend auto-selects top 6-8 documents via semantic retrieval (or uses explicit docIds)
 *   4. LLM extracts: population, intervention, comparator, outcome, sample_size, effect_size, p_value, key_finding
 *   5. User can copy markdown into a chapter, or close
 */
export default function FindingsTableDialog({ open, onOpenChange, userId, projectId, language = 'en' }) {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [savingTable, setSavingTable] = useState(false);

  const saveAsTable = async () => {
    if (!result?.markdown) return;
    setSavingTable(true);
    try {
      const t = await thesisApi.saveTable(userId, projectId, {
        caption: `Comparison of reported findings on ${result.topic}`,
        markdown: result.markdown,
        note: result.summary || '',
        source: 'findings',
      });
      toast.success(`Saved as Table ${t.number} — insert it from Figures & tables`);
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSavingTable(false);
    }
  };

  const reset = () => {
    setTopic('');
    setResult(null);
  };

  const handleExtract = async () => {
    if (!topic.trim() || topic.trim().length < 8) {
      toast.error('Provide a research topic of at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const data = await thesisApi.extractFindingsTable(userId, projectId, topic.trim(), [], [], language);
      setResult(data);
      if (!data.rows || data.rows.length === 0) {
        toast.warning('No matching sources found for this topic. Try a broader query.');
      } else {
        toast.success(`Extracted ${data.rows.length} sources`);
      }
    } catch (e) {
      console.error(e);
      toast.error('Extraction failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  const copyMarkdown = () => {
    if (!result?.markdown) return;
    navigator.clipboard.writeText(result.markdown);
    toast.success('Markdown table copied to clipboard');
  };

  const downloadMarkdown = () => {
    if (!result?.markdown) return;
    const blob = new Blob([`# Findings table: ${result.topic}\n\n${result.markdown}\n\n## Summary\n\n${result.summary || ''}`],
      { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `findings-table-${result.topic.slice(0, 30).replace(/\s+/g, '-')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-6xl max-h-[88vh] flex flex-col" data-testid="findings-table-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif" style={{ fontFamily: '"Fraunces", Georgia, serif' }}>
            <Table2 className="w-5 h-5 text-[#1d4ed8]" />
            Findings Table Extractor
          </DialogTitle>
          <DialogDescription className="text-xs">
            Extract a comparison table across multiple papers in your library —
            study design, population, intervention, comparator, outcome, sample
            size, effect size, p-value, and key finding per source. Never fabricates numbers.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-3 border-b border-border">
          <Label htmlFor="findings-topic" className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
            Research topic
          </Label>
          <div className="flex gap-2">
            <Input
              id="findings-topic"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. early aggressive fluid resuscitation in acute pancreatitis"
              className="flex-1"
              data-testid="findings-topic-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !loading) handleExtract(); }}
            />
            <Button onClick={handleExtract} disabled={loading || !topic.trim()}
              className="bg-[#0b1a35] hover:bg-[#1d4ed8] text-white"
              data-testid="findings-extract-btn">
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Extracting...</> : 'Extract'}
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            We'll auto-select the most relevant documents from your library via semantic search.
            The AI returns <code>null</code> when a field is not reported in the source.
          </p>
        </div>

        {result && (
          <div className="flex-1 overflow-auto py-4" data-testid="findings-results">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-muted-foreground">
                Topic: <span className="font-mono text-foreground">{result.topic}</span>
                {' · '}
                <span className="font-mono text-foreground">{result.rows.length}</span> sources
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={saveAsTable} disabled={savingTable}
                  data-testid="findings-save-table-btn">
                  {savingTable ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                  Save as numbered table
                </Button>
                <Button size="sm" variant="outline" onClick={copyMarkdown} data-testid="findings-copy-btn">
                  <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy markdown
                </Button>
                <Button size="sm" variant="outline" onClick={downloadMarkdown} data-testid="findings-download-btn">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Download .md
                </Button>
              </div>
            </div>

            {result.rows.length > 0 ? (
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 dark:bg-white/[0.03]">
                    <tr>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Source</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Year</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Design</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Population</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Intervention</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Comparator</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Outcome</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">N</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Effect</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">p</th>
                      <th className="px-2 py-2 text-left font-semibold text-muted-foreground uppercase tracking-wider">Key finding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.rows.map((r, i) => (
                      <tr key={i} className={i % 2 ? 'bg-muted/30 dark:bg-white/[0.02]' : ''}>
                        <td className="px-2 py-2 align-top max-w-[200px]">
                          <div className="font-medium text-foreground truncate" title={r.source_title}>{r.source_title || '—'}</div>
                          <div className="font-mono text-[10px] text-muted-foreground truncate">{r.manubot_ref}</div>
                        </td>
                        <td className="px-2 py-2 align-top">{r.year ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top">{r.study_design ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top">{r.population ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top">{r.intervention ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top">{r.comparator ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top">{r.outcome ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top font-mono">{r.sample_size ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top font-mono">{r.effect_size ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top font-mono">{r.p_value ?? <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-2 py-2 align-top max-w-[260px] text-foreground">{r.key_finding ?? <span className="text-muted-foreground">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">No rows extracted.</p>
            )}

            {result.summary && (
              <div className="mt-5 border-l-2 border-[#1d4ed8] pl-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-[#1d4ed8] dark:text-[#7aa9ff] font-semibold mb-1">
                  Cross-study synthesis
                </div>
                <p className="text-sm text-foreground leading-relaxed">{result.summary}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
